import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  AssignLockerInput,
  CreateLockerInput,
  GetLockerHistoryOutput,
  GetLockerOutput,
  GetLockersInput,
  GetLockersOutput,
  HandoutLockerKeyInput,
  ReplaceLockerKeyInput,
  UpdateLockerInput,
  WithdrawLockerKeyInput,
} from './locker-api.types';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { Locker } from './locker-api.class';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({ providedIn: 'root' })
export class LockerApiService {
  private resource = `${environment.api.avs}/Spind`;

  constructor(
    private httpClient: HttpClient,
    private message: NzMessageService,
  ) {}

  getAllLockers(input: GetLockersInput): Observable<GetLockerOutput[]> {
    const url = `${this.resource}`;
    const params = new HttpParams({ fromObject: input });

    return this.httpClient
      .get<GetLockersOutput>(url, {
        params: params,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of([]); // Return empty array instead of throwing an 404 error
          } else {
            this.message.error(
              'Etwas beim Laden der Spinde ist schiefgelaufen',
            );
            return of([]);
          }
        }),
      );
  }

  searchLocker(input: GetLockersInput): Observable<GetLockersOutput> {
    const url = `${this.resource}`;
    const params = new HttpParams({ fromObject: input });

    return this.httpClient.get<GetLockersOutput>(url, {
      params: params,
    });
  }

  getLocker(id: number): Promise<Locker> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(
      this.httpClient
        .get<GetLockerOutput>(url, {})
        .pipe(map((result) => new Locker(result))),
    );
  }

  createLocker(input: CreateLockerInput) {
    const url = `${this.resource}`;

    return firstValueFrom(this.httpClient.post(url, input, {}));
  }

  updateLocker(id: number, input: UpdateLockerInput): Promise<any> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(this.httpClient.put(url, input, {}));
  }

  deleteLocker(id: number): Promise<any> {
    const url = `${this.resource}/${id}`;

    return firstValueFrom(this.httpClient.delete(url));
  }

  assignLocker(id: number, input: AssignLockerInput): Promise<any> {
    const url = `${this.resource}/${id}/zuweisen`;

    return firstValueFrom(this.httpClient.patch(url, input));
  }

  releaseLocker(id: number): Promise<any> {
    const url = `${this.resource}/${id}/freigeben`;

    return firstValueFrom(this.httpClient.patch(url, {}));
  }

  handoutLockerKey(id: number, input: HandoutLockerKeyInput): Promise<any> {
    const url = `${this.resource}/${id}/Schluessel/ausgeben`;

    return firstValueFrom(this.httpClient.patch(url, input));
  }

  withdrawLockerKey(id: number, input: WithdrawLockerKeyInput): Promise<any> {
    const url = `${this.resource}/${id}/Schluessel/zuruecknehmen`;

    return firstValueFrom(this.httpClient.patch(url, input));
  }

  replaceLock(id: number, input: ReplaceLockerKeyInput): Promise<any> {
    const url = `${this.resource}/${id}/Schluessel/ersetzen`;

    return firstValueFrom(this.httpClient.patch(url, input));
  }

  getLockerHistory(id: number): Promise<GetLockerHistoryOutput> {
    const url = `${this.resource}/${id}/historie`;

    return firstValueFrom(this.httpClient.get<GetLockerHistoryOutput>(url, {}));
  }
}
